import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './Disbursement.module.scss'
import useAuth from '../../components/Hooks/useAuth';
import { formatNumberWithComma, getLocalUser } from '../../components/functions/utilities';
import { UsersRestS } from '../../rest/users-rest-service';
import { Route, Routes, useLocation, useParams } from 'react-router';
import { Outlet, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import DisbursementHeader from './DisbursementHeader/DisbursementHeader';
import { DisbursementFormSteps, DisbursementFormStepsTitle, LendingStatus, ToastifyStatus, UnderwritingFormStatus, disbursementRoutes } from '../../interfaces/interfaces';
import { styled } from '@mui/system';
import Stepper from '@mui/material/Stepper';
import { Step, StepButton, StepConnector, StepIconProps, StepLabel, stepConnectorClasses } from '@mui/material';
import { HiChevronLeft as LeftChevron, HiChevronRight as RightChevron, HiPencil as PencilIcon } from 'react-icons/hi';
import { HiCheck as CheckIcon } from 'react-icons/hi';
import VerifySupplier from './VerifySupplier/VerifySupplier';
import MeritoButton from '../../components/common/MeritoButtons/MeritoButtons';
import InvoiceProducts from './InvoiceProducts/InvoiceProducts';
import { DisbursementFormContext } from './context/DisbursementFormContext';
import { RestS } from '../../rest/rest-service';
import { NotificationMsg } from '../../components/layout';
import { toast } from 'react-toastify';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { FaCircleExclamation as Exclamation } from "react-icons/fa6";
import { useAppSelector } from '../../redux/reduxHooks';
import { RiErrorWarningFill as ErrorIcon } from 'react-icons/ri';
import { isError } from 'lodash';

const stepperList = [
    {
        name: DisbursementFormSteps.VERIFY_SUPPLIER,
        title: DisbursementFormStepsTitle.VERIFY_SUPPLIER,
        link: `${disbursementRoutes.disbursementVerifySupplier}`
    },
    {
        name: DisbursementFormSteps.INVOICE_PRODUCTS,
        title: DisbursementFormStepsTitle.INVOICE_PRODUCTS,
        link: `${disbursementRoutes.disbursementInvoiceProducts}`
    }
]

const DisbursementStepper = styled(Stepper)({
    // Target the alternative label style for all StepLabel components within this Stepper
    '.MuiStepLabel-labelContainer .MuiStepLabel-label': {
        marginTop: '0.5rem !important', // Remove the margin-top
    },
});

const DisbursementConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.active}`]: {
        '& .MuiStepConnector-line': {
            borderColor: '#100828',
            borderWidth: '2px'
        },
    },
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 14,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
    },
    [`&.${stepConnectorClasses.root}`]: {
        '& .MuiStepConnector-line': {
            borderColor: '#d4d4d4',
            borderWidth: '2px',
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        '& .MuiStepConnector-line': {
            borderColor: '#d4d4d4',
            borderWidth: '2px'
        },
    }
}));

const UnderwritingStepIcon = (props) => {
    const { active, completed, icon, disabled } = props;

    // console.log('icon', icon, disabled, active)


    return (
        <div className={`${styles.customStepIcon} ${disabled && !active ? styles.disabled : completed ? styles.completed : active ? styles.active : ''} `}>
            {active ?
                <div>
                    <PencilIcon />
                </div>
                :
                completed ?
                    <div>
                        <CheckIcon />
                    </div>
                    :
                    <div className={`${styles.numberIcon} ${active || completed ? styles.active : ''}`}>
                        {icon}
                    </div>
            }
        </div>
    );
}

const DisbursementSections = () => {
    const [activeStep, setActiveStep] = useState(0);
    const formSectionRefs = useRef({
        [DisbursementFormSteps.VERIFY_SUPPLIER]: React.createRef(),
        [DisbursementFormSteps.INVOICE_PRODUCTS]: React.createRef(),
    });
    const { formData, isBtnEnabled, setFormData, handleAndSetFormStatuses, formCompleteStatus, formPristine } = useContext(DisbursementFormContext);
    const navigate = useNavigate();
    const [submitLoading, setSubmitLoading] = useState(false);

    const { formId } = useParams();
    const { pathname } = useLocation();
    const [showSaveWarning, setShowSaveWarning] = useState(false);

    const [totalAmount, setTotalAmount] = useState(0);

    const notify = (
        notification: any,
        error: boolean = false,
        config: any = {},
    ) => {
        toast(
            ({ closeToast }) => (
                <NotificationMsg
                    closeToast={closeToast}
                    onClickFlashMessage={() => { }}
                    notification={notification}
                />
            ),
            {
                hideProgressBar: config.showProgressBar
                    ? !config.showProgressBar
                    : true,
                icon: false,
                closeButton: false,
                style: {
                    top: 100,
                    padding: 0,
                },
                bodyStyle: {
                    padding: 0,
                    margin: 0,
                },
                position: "top-right",
                autoClose: error
                    ? false
                    : config.autoCloseTime
                        ? config.autoCloseTime
                        : 10000,
                onClose: () => (config.closeAction ? config.closeAction() : null),
            },
        );
    };


    const handleNextStep = async () => {
        let isFormError = false;
        try {

            const isLastStep = stepperList.length - 1 == activeStep;
            const currentStep = activeStep;
            const nextStep = activeStep + 1;
            const currentStepObj = stepperList[currentStep];
            const currentFormValues = await formSectionRefs?.current[currentStepObj?.name].current.submitForm();
            if (!currentFormValues || !currentFormValues?.success || !currentFormValues?.values) {
                return {};
            } else {
                console.log('formValues', currentFormValues)
                if (currentStepObj?.name == DisbursementFormSteps.VERIFY_SUPPLIER) {
                    let submitFormData = {
                        formId: formId,
                        step: currentStepObj?.name,
                        formData: {
                            verifiedProducts: currentFormValues?.values ? currentFormValues?.values['verifiedProducts'] : []
                        }
                    }
                    if (!currentFormValues?.pristine || formCompleteStatus[currentStepObj.name] == UnderwritingFormStatus.INCOMPLETE) {
                        setSubmitLoading(true);
                        const isSuccess = await onSubmitForm(submitFormData, isLastStep);
                        setSubmitLoading(false);
                    }
                    navigate(`/disbursement/${formId}/${disbursementRoutes.disbursementInvoiceProducts}`);
                    setActiveStep(nextStep);
                } else if (currentStepObj?.name == DisbursementFormSteps.INVOICE_PRODUCTS) {
                    // submit last step data
                    let submitFormData = {
                        formId: formId,
                        step: currentStepObj?.name,
                        formData: {
                            invoiceProducts: currentFormValues?.values ? currentFormValues?.values['invoiceProducts'] : []
                        }
                    }
                    if (!currentFormValues?.pristine || formCompleteStatus[currentStepObj.name] == UnderwritingFormStatus.INCOMPLETE) {
                        setSubmitLoading(true);
                        const isSuccess = await onSubmitForm(submitFormData, isLastStep);
                        setSubmitLoading(false);
                    }
                    navigate(`${disbursementRoutes.disbursementSections}/${disbursementRoutes.disbursementSuccess}/${formId}`, {
                        replace: true
                    });
                }
            }
        } catch (error) {
            console.log('error', error);
            setSubmitLoading(false);
            notify({
                notificationBody: {
                    title: "Form Submission Failed",
                    status: ToastifyStatus.failure,
                    subTitle: "There has been a technical error.",
                    // buttonText: "Contact Support",
                    // buttonAction: () => {
                    //     navigate("/Support");
                    // },
                },
            });
        }
    }

    const { integrationPartners } = useAppSelector((state) => state.config);

    const { connectionStatus } = useAppSelector((state) => state.connections);

    const ipConfigs = useMemo(() => {
        if (integrationPartners != undefined) {
            let configObj = {};
            const connectionStatusSet = new Set(connectionStatus);

            for (const ip of Object.values(integrationPartners)) {
                // console.log('ip', ip)
                const logo = ip['logo'];
                const ipId = ip["_id"]
                // if(connectionStatusSet.has(ip['_id'])) {
                configObj[ip['companyName']] = {
                    connectionStatus: connectionStatusSet.has(ipId) ? true : false,
                    logo
                };
                // } 
            }
            return configObj;
        } else {
            return {};
        }
    }, [integrationPartners, connectionStatus]);

    const onSubmitForm = async (submitFormData, isLastStep = false) => {
        try {
            const data: any = await RestS.submitDisbursementFormData({
                ...submitFormData,
                applicationStatus: isLastStep ? LendingStatus.PENDING_APPROVAL : LendingStatus.IN_PROGRESS
            });
            handleAndSetFormStatuses(data?.formStatuses)
            setFormData(data?.formData['data']);
            return true;
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    }

    const stepperLocationObj = useMemo(() => {
        return stepperList.reduce((prev, curVal, index) => {
            prev[`/disbursement/${formId}/${curVal.link}`] = {
                ...curVal,
                stepIndex: index
            }
            return prev;
        }, {})
    }, [stepperList, formId])

    useEffect(() => {
        let activeStepIndex = stepperLocationObj[pathname.toLowerCase()];
        if (activeStepIndex != undefined) {
            setActiveStep(activeStepIndex?.stepIndex)
        } else {
            setActiveStep(0)
        }
    }, [pathname]);

    const allowSteps = useMemo(() => {
        const allSteps = Object.keys(formCompleteStatus);
        const allowedSteps = new Set();
        for (const step of allSteps) {
            if (formCompleteStatus[step] == UnderwritingFormStatus.COMPLETED) {
                allowedSteps.add(step);

            } else {
                allowedSteps.add(step);
                break;
            }
        }
        return allowedSteps;
    }, [formCompleteStatus]);



    // const btnDisabledState = useMemo(() => {
    //     return isBtnEnabled;
    // }, [isBtnEnabled, activeStep])

    const footerBannerData = useMemo(() => {
        const { invoiceAmount } = formData?.invoiceDetails ?? { invoiceAmount: 0 };
        const isError = Number(totalAmount) != Number(invoiceAmount);
        const isOver = Number(totalAmount) > Number(invoiceAmount);
        return {
            totalAmount: formatNumberWithComma(totalAmount),
            isError: isError,
            balance: {
                amount: `₹ ${formatNumberWithComma(Math.abs(Number(invoiceAmount) - Number(totalAmount)))} ${isError ? isOver ? 'over' : 'left' : 'left'}`,
                isOver: isOver,
            }
        }
    }, [totalAmount, formData])

    return (
        <>

            <div className={styles.rootContainer}>
                <DisbursementHeader actionBtn={<MeritoButton
                    type="primary-1"
                    size='md2'

                    icon={<RightChevron />} isIconToRight={true}
                    disabled={!isBtnEnabled}
                    loading={submitLoading} btnText={stepperList.length - 1 == activeStep ? "Submit" : "Next"} onClick={() => {
                        handleNextStep();
                    }} />
                } />
                <div className={styles.bodyContainer}>
                    <div className={styles.innerBodyContainer}>
                        <div className={styles.stepperContainer}>
                            <div className={styles.innerStepperContainer}>
                                <DisbursementStepper connector={<DisbursementConnector />} alternativeLabel nonLinear activeStep={activeStep}>
                                    {stepperList.map((item, index) => {
                                        const isCompleted = formCompleteStatus[item?.name] == UnderwritingFormStatus.COMPLETED;
                                        return (
                                            <Step completed={isCompleted} key={`${item.name}_${index}`} onClick={(e) => {
                                                if ((isCompleted && activeStep != index) || allowSteps.has(item.name)) {
                                                    navigate(`/disbursement/${formId}/${item.link}`)
                                                    // handleRandomNavigation({
                                                    //     stepIndex: index,
                                                    //     link: item.link
                                                    // })
                                                }
                                            }}>
                                                <StepLabel StepIconComponent={(props) => <UnderwritingStepIcon {...props} />}>
                                                    <div className={`${styles.customStepLabel} ${activeStep == index ? styles.active : ''}`}>
                                                        {item.title}
                                                    </div>
                                                </StepLabel>
                                            </Step>
                                        )
                                    })}
                                </DisbursementStepper>
                            </div>
                        </div>

                        <Routes>
                            <Route path={`/${disbursementRoutes.disbursementVerifySupplier}`} element={<VerifySupplier ipConfigs={ipConfigs} ref={formSectionRefs?.current[DisbursementFormSteps.VERIFY_SUPPLIER]} />} />
                            <Route path={`/${disbursementRoutes.disbursementInvoiceProducts}`} element={<InvoiceProducts setTotalAmount={setTotalAmount} ipConfigs={ipConfigs} ref={formSectionRefs?.current[DisbursementFormSteps.INVOICE_PRODUCTS]} />} />
                        </Routes>
                    </div>
                    {location?.pathname.includes(disbursementRoutes.disbursementInvoiceProducts) ?
                        <div className={styles.footer}>
                            <div className={styles.innerFooterContainer}>
                                {footerBannerData.isError ? <div className={styles.errorBannerContainer}>
                                    <div className={styles.iconContainer}>
                                        <ErrorIcon />
                                    </div>
                                    <div className={styles.errorText}>
                                        Total added costs do not match the invoice amount!
                                    </div>
                                </div> : null}

                                <div className={styles.footerText}>
                                    <div className={styles.totalText}>
                                        <div className={styles.totalLabel}>
                                            Total Added
                                        </div>
                                        <div className={styles.totalAmount}>
                                            {footerBannerData?.totalAmount}
                                        </div>
                                    </div>
                                    <div className={`${styles.amountLeft} ${footerBannerData?.isError ? footerBannerData?.balance?.isOver ? styles.red : styles.left : styles.success}`}>
                                        {footerBannerData?.balance?.amount}
                                    </div>
                                </div>
                            </div>
                        </div> : null}

                </div>
            </div>

            {/* <ConfirmDialog
                modalStyles={{ maxWidth: '450px' }}
                showButton={false}
                visible={showSaveWarning}
                toggle={() => {
                    setShowSaveWarning(!showSaveWarning);
                }}
                showCloseBtn={false}
                size={"md"}
            >
                <div className={styles.confirmationContainer}>
                    <div className={styles.topContainer}>
                        <div className={styles.iconContainer}>
                            <Exclamation />
                        </div>
                        <div className={styles.title}>
                            You Have Unsaved Changes!
                            <br />
                            <br />
                        </div>
                    </div>

                    <div className={styles.bottomContainer}>
                        <MeritoButton onClick={() => {
                            // setShowSaveWarning(false)
                            // navigat
                            if (navigateToState.stepIndex != null) {
                                setActiveStep(navigateToState.stepIndex);
                                navigate(navigateToState.link);
                            }
                            setShowSaveWarning(false);
                        }} size="lg" isRounded={true} btnType="button" type="secondary-2" btnText={'Discard Changes'} />
                        <MeritoButton size="lg" onClick={() => {
                            // submitBankStatementData();
                            setShowSaveWarning(false);
                        }} isRounded={true} btnType="button" type="primary-1" btnText="Continue Editing" />
                    </div>
                </div>
            </ConfirmDialog> */}
        </>
    )
}

export default DisbursementSections;